/**
 * Where each exercise is printed, so it can be clicked on the page itself.
 *
 * This is the join between two things that already existed and did not know about each other.
 * `pdf-layout.ts` reports a box per line of extracted text; `ingest.ts` reports which lines
 * became which exercise. Put together they say: 1042 is these four rectangles, on page 3.
 *
 * Nothing here guesses. A region exists only where a line index resolves to a box that was
 * actually measured off the page, which is what makes a highlight trustworthy — and it is why
 * `resolutionOf` reports the misses rather than inventing a rectangle for them. A click target
 * over the wrong paragraph is worse than no click target: the exercise that lands on the sheet
 * would be a different one from the exercise under the cursor.
 *
 * Pure, and free of pdfjs, so the whole mapping is exercisable in Vitest.
 */
import type { Box, PlacedLine } from './pdf-layout';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * One exercise, on one page.
 *
 * An exercise that runs from the bottom of a column onto the next page produces two regions with
 * the same number — both clickable, both highlighting when either is hovered, because to a reader
 * they are one question printed in two places.
 */
export interface Region {
  n: number;
  page: number;
  rects: Rect[];
}

/** The shape this module needs from a parsed exercise. Deliberately not the whole record. */
export interface Placed {
  n: number;
  lines: { from: number; to: number };
}

/**
 * Lines close together in the same column become one rectangle.
 *
 * Highlighting eleven separate line boxes reads as a striped mess and puts eleven elements in the
 * DOM per exercise. Merging by column keeps the shape a reader recognises — one block per column
 * the question occupies — and a question that jumps the gutter still gets its two blocks rather
 * than one rectangle spanning the gutter and covering its neighbour.
 */
const OVERLAP = 0.35;
/** A vertical gap wider than this many line heights is a different block, not the next line. */
const GAP = 1.9;

export function mergeRects(rects: readonly Rect[]): Rect[] {
  const sorted = [...rects].sort((a, b) => a.y - b.y || a.x - b.x);
  const out: Rect[] = [];
  for (const rect of sorted) {
    const last = out[out.length - 1];
    if (last) {
      const from = Math.max(last.x, rect.x);
      const to = Math.min(last.x + last.w, rect.x + rect.w);
      const shared = (to - from) / Math.min(last.w, rect.w);
      const gap = rect.y - (last.y + last.h);
      if (shared >= OVERLAP && gap <= rect.h * GAP) {
        const right = Math.max(last.x + last.w, rect.x + rect.w);
        const bottom = Math.max(last.y + last.h, rect.y + rect.h);
        last.x = Math.min(last.x, rect.x);
        last.y = Math.min(last.y, rect.y);
        last.w = right - last.x;
        last.h = bottom - last.y;
        continue;
      }
    }
    out.push({ ...rect });
  }
  return out;
}

/** A little air, so the highlight frames the text instead of clipping its ascenders. */
const PAD = 2;

const grow = (rect: Rect): Rect => ({
  x: rect.x - PAD,
  y: rect.y - PAD,
  w: rect.w + PAD * 2,
  h: rect.h + PAD * 2,
});

/**
 * Every exercise's regions, in page order.
 *
 * `exercises` and `placed` must come from the same read: the line indices are indices into the
 * text that extraction produced, and feeding a parse of one document's text against another
 * document's boxes is the one way to get a confidently wrong answer out of this.
 */
export function regionsFor(
  exercises: readonly Placed[],
  placed: readonly PlacedLine[],
): Region[] {
  const out: Region[] = [];
  for (const exercise of exercises) {
    const byPage = new Map<number, Box[]>();
    const to = Math.min(placed.length, Math.max(exercise.lines.from, exercise.lines.to));
    for (let i = Math.max(0, exercise.lines.from); i < to; i++) {
      for (const box of placed[i]?.boxes ?? []) {
        const here = byPage.get(box.page);
        if (here) here.push(box);
        else byPage.set(box.page, [box]);
      }
    }
    for (const [page, boxes] of [...byPage].sort((a, b) => a[0] - b[0])) {
      const rects = mergeRects(boxes.map(box => ({ x: box.x, y: box.y, w: box.w, h: box.h })))
        .map(grow);
      if (rects.length > 0) out.push({ n: exercise.n, page, rects });
    }
  }
  return out.sort((a, b) => a.page - b.page || a.n - b.n);
}

export interface Resolution {
  /** How many exercises got at least one region. */
  resolved: number;
  total: number;
  /** The numbers that got none, so a surface can name them rather than report a percentage. */
  missing: number[];
}

/**
 * How much of a parse became clickable.
 *
 * Reported rather than assumed. A book whose exercises mostly do not resolve is one where the
 * pane has to fall back to selecting text by hand, and the owner should be told that before he
 * clicks at a page and nothing happens.
 */
export function resolutionOf(exercises: readonly Placed[], regions: readonly Region[]): Resolution {
  const found = new Set(regions.map(region => region.n));
  const missing = exercises.filter(exercise => !found.has(exercise.n)).map(exercise => exercise.n);
  return { resolved: exercises.length - missing.length, total: exercises.length, missing };
}

/** The regions printed on one page. */
export const regionsOn = (regions: readonly Region[], page: number): Region[] =>
  regions.filter(region => region.page === page);

/**
 * The region under a point, in page points.
 *
 * Smallest-first, because a question printed inside a shaded example box would otherwise be
 * beaten by whatever encloses it. Ties do not happen in practice; sorting makes them harmless.
 */
export function regionAt(regions: readonly Region[], page: number, x: number, y: number): Region | null {
  const hits = regionsOn(regions, page).filter(region =>
    region.rects.some(rect => x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h));
  if (hits.length === 0) return null;
  const area = (region: Region) => region.rects.reduce((sum, rect) => sum + rect.w * rect.h, 0);
  return hits.sort((a, b) => area(a) - area(b))[0]!;
}
